import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import {
  FormSelect,
  FormSelectOptions
} from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { User } from '../../../../../services/endpoints/people/user';
import { useGetVenuesQuery } from '../../../../../services/endpoints/places/venue';
import { useGetCoursesQuery } from '../../../../../services/endpoints/schedule/course';
import { useToggleInstructorCourseRequestMutation } from '../../../../../services/endpoints/schedule/instructorCourseRequest';
import { getCurrentUser } from '../../../../../services/helper';
import { Course } from '../../../../../types/course.type';
import { needsInstructorColumns } from '../../../../../utils/consts/columnDefs/sessionColumns';
import { sessionStatus, sessionTypes } from '../../../../../utils/consts/schedule/sessions';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';

const defaultScope = 'needs_instructors';
export const currentUser = getCurrentUser();

export const userCanView_Needs = (user: User | null) => {
  if (
    user?.isSuper ||
    user?.isSupremeAdmin ||
    user?.isAdmin ||
    user?.isGlobal ||
    user?.isLocationAdmin ||
    user?.isInstructor
  ) {
    return true;
  }

  return false;
};

const hasRequestedSession = (course: Course, instructorId?: number) =>
  course.instructor_course_requests?.some((request) => request.instructor_id === instructorId) ??
  false;

export const NeedsView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl(
    {
      scope: defaultScope,
      omitScopeCount: true
    },
    { omitScopeFromUrl: true }
  );
  const { data, error, isLoading, isFetching } = useGetCoursesQuery(filters);
  const [toggleSessionRequestMutation] = useToggleInstructorCourseRequestMutation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [pendingCourseId, setPendingCourseId] = useState<number | null>(null);

  const { data: venueOptionsData } = useGetVenuesQuery();

  let venueOptions: FormSelectOptions[] = [];

  if (venueOptionsData) {
    venueOptions = venueOptionsData.results.map((v) => {
      let ret: FormSelectOptions = {
        value: v.id,
        label: v.title
      };

      return ret;
    });
  }

  const statusOptions = Object.entries(sessionStatus).map((status) => ({
    value: status[0],
    label: status[1]
  }));
  const courseTypeOptions = Object.entries(sessionTypes).map((type) => ({
    value: type[0],
    label: type[1]
  }));

  const filterFormFields: FilterField[] = [
    {
      name: 'title',
      label: 'Title',
      value: getFilterValue(filters.data?.filterBy, 'title')
    },
    {
      name: 'status',
      element: (
        <FormSelect
          name="status"
          label="Status"
          options={statusOptions}
          selected_Value={statusOptions.find(
            (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'status')
          )}
        />
      )
    },
    {
      name: 'course_type',
      element: (
        <FormSelect
          name="course_type"
          label="Session Type"
          options={courseTypeOptions}
          selected_Value={courseTypeOptions.find(
            (option) =>
              String(option.value) === getFilterValue(filters.data?.filterBy, 'course_type')
          )}
        />
      )
    },
    {
      name: 'venue_title',
      label: 'Venue',
      value: getFilterValue(filters.data?.filterBy, 'venue_title')
    },
    {
      name: 'instructor_full_name',
      label: 'Instructor',
      value: getFilterValue(filters.data?.filterBy, 'instructor_full_name')
    },
    {
      name: 'starts_from',
      id: 'starts_on',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="starts_from"
          label="Starts on"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'starts_on', 'greater_equal_than')
    },
    {
      name: 'starts_to',
      id: 'starts_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="starts_to" />,
      value: getFilterValue(filters.data?.filterBy, 'starts_on', 'lesser_equal_than')
    },
    {
      name: 'ends_from',
      id: 'ends_on',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="ends_from"
          label="Ends on"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'ends_on', 'greater_equal_than')
    },
    {
      name: 'ends_to',
      id: 'ends_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="ends_to" />,
      value: getFilterValue(filters.data?.filterBy, 'ends_on', 'lesser_equal_than')
    },
    {
      name: 'day_of_week',
      label: 'Day of Week',
      value: getFilterValue(filters.data?.filterBy, 'day_of_week')
    }
  ];

  if (currentUser?.isGlobal) {
    filterFormFields.splice(1, 0, {
      name: 'location_id',
      type: 'number',
      label: 'Location',
      element: (
        <FormSelect
          name="location_id"
          label="Location"
          options={locationOptions}
          selected_Value={locationOptions.find(
            (option) =>
              String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
          )}
        />
      )
    });
  }

  useEffect(() => {
    if (!isLoading && error) {
      const apiError = error as { data?: { message?: string; error?: string[] } };
      addToast(
        apiError?.data?.message ??
          apiError?.data?.error?.join(', ') ??
          'Error while loading schedule needs',
        'error'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setCourses(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  const handleToggleSessionRequest = (course: Course) => {
    if (!currentUser?.id) {
      return;
    }

    const cancel = hasRequestedSession(course, currentUser.id);
    setPendingCourseId(course.id);

    toggleSessionRequestMutation({
      course_id: course.id,
      instructor_id: currentUser.id,
      cancel
    })
      .unwrap()
      .then((success) => {
        if (!success) {
          addToast(
            cancel
              ? 'Unable to cancel your session request.'
              : 'Unable to request this session. You may have already requested it.',
            'error'
          );
          return;
        }

        addToast(
          cancel ? 'Your session request has been cancelled.' : 'Your session request has been sent.',
          'success'
        );
      })
      .catch((requestError: { data?: { message?: string } }) => {
        addToast(
          requestError?.data?.message ??
            (cancel ? 'Failed to cancel session request.' : 'Failed to request session.'),
          'error'
        );
      })
      .finally(() => {
        setPendingCourseId(null);
      });
  };

  const requestCol: ColumnDef<Course> | null = currentUser?.isInstructor
    ? {
        header: '',
        accessorKey: 'session_request',
        enableSorting: false,
        cell: ({ row }) => {
          const requested = hasRequestedSession(row.original, currentUser?.id);
          const isPending = pendingCourseId === row.original.id;

          return (
            <SimpleButton
              height={30}
              className="btn-sm text-center text-nowrap"
              variant={requested ? 'secondary' : 'primary'}
              disabled={isPending}
              onClick={() => handleToggleSessionRequest(row.original)}
            >
              {isPending ? 'Saving...' : requested ? 'Cancel Request' : 'Request Session'}
            </SimpleButton>
          );
        }
      }
    : null;

  const columns = requestCol ? needsInstructorColumns.concat(requestCol) : needsInstructorColumns;

  return (
    <div className="py-2">
      <SFCTable
        name={`Available Sessions`}
        columns={columns}
        data={courses}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters?.data?.filterBy}
        indexDownloadPath="courses"
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
